import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck } from "lucide-react";

/**
 * Globale Allowlist – nur sichtbar für Super-Admin (is_super_admin=true).
 * Erlaubt firmenübergreifende E-Mail-Freigaben (z.B. Plattform-Admins).
 */
export function SettingsGlobalAllowlistSection({ isSuperAdmin }) {
  const [items, setItems] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("advisor");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin) return;
    apiClient
      .get("/admin/global-allowlist")
      .then((r) => {
        setItems(r.data || []);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [isSuperAdmin]);

  if (!isSuperAdmin) return null;

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSaving(true);
    setError("");
    try {
      const res = await apiClient.post("/admin/global-allowlist", {
        email: email.trim().toLowerCase(),
        role,
        note: note || null,
      });
      setItems((prev) => {
        const without = prev.filter((p) => p.id !== res.data.id);
        return [res.data, ...without];
      });
      setEmail("");
      setNote("");
    } catch (e) {
      setError(e.response?.data?.detail || "Eintrag konnte nicht angelegt werden.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (id) => {
    setError("");
    try {
      await apiClient.delete(`/admin/global-allowlist/${id}`);
      setItems((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      setError(e.response?.data?.detail || "Eintrag konnte nicht entfernt werden.");
    }
  };

  return (
    <Card
      id="section-global-allowlist"
      className="p-4 border border-slate-200 bg-white shadow-sm scroll-mt-20"
      data-testid="settings-global-allowlist-card"
    >
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="flex items-start gap-3">
          <div className="h-8 w-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="h-4 w-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-medium text-slate-900">
                Globale Allowlist
              </h2>
              <Badge
                variant="outline"
                className="text-[10px] h-5 bg-emerald-50 text-emerald-700 border-emerald-200"
              >
                Super-Admin
              </Badge>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              E-Mails, die firmenübergreifend Zugang zur Plattform erhalten. Nur du
              als Super-Admin kannst diese Liste pflegen. Pro-Firma-Freigaben werden
              im jeweiligen Firmen-Tab ("Zugriff für Treuhänder") gepflegt.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          {error}
        </div>
      )}

      <form
        className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-4"
        onSubmit={handleAdd}
        data-testid="global-allowlist-form"
      >
        <div className="md:col-span-5 space-y-1">
          <Label htmlFor="ga-email">E-Mail</Label>
          <Input
            id="ga-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="global-allowlist-email-input"
            required
          />
        </div>
        <div className="md:col-span-2 space-y-1">
          <Label htmlFor="ga-role">Rolle</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger id="ga-role" data-testid="global-allowlist-role-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="advisor">Treuhänder</SelectItem>
              <SelectItem value="viewer">Nur Lesen</SelectItem>
              <SelectItem value="owner">Owner</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3 space-y-1">
          <Label htmlFor="ga-note">Notiz (optional)</Label>
          <Input
            id="ga-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="z.B. Plattform-Admin"
            data-testid="global-allowlist-note-input"
          />
        </div>
        <div className="md:col-span-2 flex items-end">
          <Button
            type="submit"
            size="sm"
            className="w-full rounded-full bg-slate-900 text-slate-50"
            disabled={saving}
            data-testid="global-allowlist-add-button"
          >
            {saving ? "Speichern …" : "Hinzufügen"}
          </Button>
        </div>
      </form>

      <Table data-testid="global-allowlist-table">
        <TableHeader>
          <TableRow>
            <TableHead>E-Mail</TableHead>
            <TableHead className="w-32">Rolle</TableHead>
            <TableHead>Notiz</TableHead>
            <TableHead className="w-24 text-right">Aktion</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loaded && items.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={4}
                className="text-center text-sm text-slate-400"
                data-testid="global-allowlist-empty"
              >
                Noch keine globalen Freigaben.
              </TableCell>
            </TableRow>
          )}
          {items.map((item) => (
            <TableRow key={item.id} data-testid={`global-allowlist-row-${item.id}`}>
              <TableCell className="font-medium">{item.email}</TableCell>
              <TableCell className="capitalize text-slate-600">{item.role}</TableCell>
              <TableCell className="text-xs text-slate-500">{item.note || "—"}</TableCell>
              <TableCell className="text-right">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                  onClick={() => handleRemove(item.id)}
                  data-testid={`global-allowlist-remove-${item.id}`}
                >
                  Entfernen
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
